import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { LeadsGuard } from './shared/guards/leads.guard';
import { AdminGuard } from './shared/guards/admin.guard';
import { AuthGuard } from './shared/guards/auth.guard';
import { SurveysGuard } from './shared/guards/surveys.guard';
import { SupervisorGuard } from './shared/guards/supervisor.guard';
import { PendingRedirectGuard } from './shared/guards/pending-redirect.guard';
import { MenuGuard } from './shared/guards/menu.guard';
import { FeatureGuard } from './shared/guards/feature.guard';
import { AttendanceGuard } from './shared/guards/attendance.guard';
import { MenuKey, FeatureKey } from './shared/enums';

const routes: Routes = [
    {
        path: 'home',
        loadChildren: (): Promise<any> => import('./home/home.module').then(o => o.HomeModule),
        canActivate: [AuthGuard, PendingRedirectGuard]
    },
    {
        path: 'resource-links',
        loadChildren: (): Promise<any> => import('./resource-links/resource-home.module').then(o => o.ResourceHomeModule),
        canActivate: [AuthGuard]
    },
    {
        path: 'leads',
        loadChildren: (): Promise<any> => import('./leads/leads.module').then(o => o.LeadsModule),
        canActivate: [AuthGuard, LeadsGuard]
    },
    {
        path: 'login',
        loadChildren: (): Promise<any> => import('./login/login.module').then(o => o.LoginModule)
    },
    {
        path: 'surveys',
        loadChildren: (): Promise<any> => import('./surveys/surveys.module').then(o => o.SurveysModule),
        canActivate: [AuthGuard, SurveysGuard]
    },
    {
        path: 'reports',
        loadChildren: (): Promise<any> => import('./reports/reports.module').then(o => o.ReportsModule),
        canActivate: [AuthGuard]
    },
    {
        path: 'admin',
        loadChildren: (): Promise<any> => import('./admin/admin.module').then(o => o.AdminModule),
        canActivate: [AuthGuard, AdminGuard]
    },
    {
        path: 'supervisor',
        loadChildren: (): Promise<any> => import('./supervisor/supervisor.module').then(o => o.SupervisorModule),
        canActivate: [AuthGuard, SupervisorGuard]
    },
    {
        path: 'attendance',
        loadChildren: (): Promise<any> => import('./attendance/attendance.module').then(o => o.AttendanceModule),
        canActivate: [AuthGuard, AttendanceGuard],
        runGuardsAndResolvers: 'paramsOrQueryParamsChange',
        data: { feature: FeatureKey.Attendance }
    },
    {
        // PTO / Vacation Approvals: employees request (pto-requests), managers approve
        // (pto-approve-manager) - either feature admits, the page shows what the role can do.
        path: 'pto',
        loadChildren: (): Promise<any> => import('./pto/pto.module').then(o => o.PtoModule),
        canActivate: [AuthGuard, FeatureGuard],
        data: { anyFeature: [FeatureKey.PtoRequests, FeatureKey.PtoManagerApprove] }
    },
    {
        path: 'executive',
        loadChildren: (): Promise<any> => import('./executive/executive.module').then(o => o.ExecutiveModule),
        // The module hosts two independently-gated pages (Financial Information + Financial Report
        // (Quickbooks)); each child route enforces its own feature, so the parent only needs auth.
        canActivate: [AuthGuard]
    },
    // Per-module landing/hub pages (tile dashboards). Reachable by clicking a module name in the
    // sidebar; each lazy-loads the same AreaModule, which picks its tile set from the URL segment.
    // Gated by AuthGuard only - the individual tiles + their destination routes are feature-gated.
    {
        path: 'operations/portal',
        loadChildren: (): Promise<any> => import('./operations-portal/operations-portal.module').then(o => o.OperationsPortalModule),
        canActivate: [AuthGuard, MenuGuard],
        data: { menu: MenuKey.Operations }
    },
    {
        path: 'operations/numbers-test',
        loadChildren: (): Promise<any> => import('./operations-numbers/operations-numbers.module').then(o => o.OperationsNumbersModule),
        canActivate: [AuthGuard, MenuGuard, FeatureGuard],
        data: { menu: MenuKey.Operations, feature: FeatureKey.OperationsNumbers }
    },
    {
        path: 'operations/provisions',
        loadChildren: (): Promise<any> => import('./provisions/provisions.module').then(o => o.ProvisionsModule),
        canActivate: [AuthGuard, MenuGuard, FeatureGuard],
        data: { menu: MenuKey.Operations, feature: FeatureKey.OperationsProvisions }
    },
    {
        path: 'operations',
        loadChildren: (): Promise<any> => import('./area/area.module').then(o => o.AreaModule),
        canActivate: [AuthGuard, MenuGuard],
        data: { menu: MenuKey.Operations }
    },
    {
        path: 'billing',
        loadChildren: (): Promise<any> => import('./area/area.module').then(o => o.AreaModule),
        canActivate: [AuthGuard, MenuGuard],
        data: { menu: MenuKey.Billing }
    },
    {
        path: 'sales',
        loadChildren: (): Promise<any> => import('./area/area.module').then(o => o.AreaModule),
        canActivate: [AuthGuard, MenuGuard],
        data: { menu: MenuKey.Sales }
    },
    {
        path: 'csuite',
        loadChildren: (): Promise<any> => import('./area/area.module').then(o => o.AreaModule),
        canActivate: [AuthGuard, MenuGuard],
        data: { menu: MenuKey.CSuite }
    },
    {
        // Clock Records (HR): read-only view of the tech portal's clock in/out + meal records. Any of the
        // three clock features admits; each child route narrows to its own page's feature.
        path: 'hr/clock-records',
        loadChildren: (): Promise<any> => import('./clock-records/clock-records.module').then(o => o.ClockRecordsModule),
        canActivate: [AuthGuard, MenuGuard, FeatureGuard],
        data: { menu: MenuKey.Hr, anyFeature: [FeatureKey.ClockRecords, FeatureKey.ClockCompliance, FeatureKey.ClockPayrollExport] }
    },
    {
        path: 'hr',
        loadChildren: (): Promise<any> => import('./area/area.module').then(o => o.AreaModule),
        canActivate: [AuthGuard, MenuGuard],
        data: { menu: MenuKey.Hr }
    },
    // IS&T pages. Each is independently grantable, so each route carries its own FeatureGuard;
    // declared BEFORE the bare 'ist' hub route (the Operations block's ordering precedent).
    {
        path: 'ist/onboarding',
        loadChildren: (): Promise<any> => import('./ist/ist.module').then(o => o.IstOnboardingModule),
        canActivate: [AuthGuard, MenuGuard, FeatureGuard],
        data: { menu: MenuKey.Ist, feature: FeatureKey.IstOnboarding }
    },
    {
        path: 'ist/accounts',
        loadChildren: (): Promise<any> => import('./ist/ist.module').then(o => o.IstAccountsModule),
        canActivate: [AuthGuard, MenuGuard, FeatureGuard],
        data: { menu: MenuKey.Ist, feature: FeatureKey.IstAccounts }
    },
    {
        path: 'ist/password-resets',
        loadChildren: (): Promise<any> => import('./ist/ist.module').then(o => o.IstPasswordResetsModule),
        canActivate: [AuthGuard, MenuGuard, FeatureGuard],
        data: { menu: MenuKey.Ist, feature: FeatureKey.IstPasswordReset }
    },
    {
        // The IS&T TEAM's ticket queue (respond, close, reopen). Submitting a request happens on
        // the all-users /support page below.
        path: 'ist/support',
        loadChildren: (): Promise<any> => import('./ist/ist.module').then(o => o.IstSupportModule),
        canActivate: [AuthGuard, MenuGuard, FeatureGuard],
        data: { menu: MenuKey.Ist, feature: FeatureKey.IstSupport }
    },
    {
        path: 'ist/documents',
        loadChildren: (): Promise<any> => import('./portal-documents/portal-documents.module').then(o => o.PortalDocumentsModule),
        canActivate: [AuthGuard, MenuGuard, FeatureGuard],
        data: { menu: MenuKey.Ist, feature: FeatureKey.PortalDocumentsManage }
    },
    {
        path: 'ist',
        loadChildren: (): Promise<any> => import('./area/area.module').then(o => o.AreaModule),
        canActivate: [AuthGuard, MenuGuard],
        data: { menu: MenuKey.Ist }
    },
    {
        path: 'administrator',
        loadChildren: (): Promise<any> => import('./area/area.module').then(o => o.AreaModule),
        canActivate: [AuthGuard, MenuGuard],
        data: { menu: MenuKey.Administrator }
    },
    {
        // IS&T Support for EVERY signed-in user: submit a support request + follow its thread.
        // Deliberately AuthGuard only - no menu or feature gate - anyone can need IS&T help.
        path: 'support',
        loadChildren: (): Promise<any> => import('./support/support.module').then(o => o.SupportModule),
        canActivate: [AuthGuard]
    },
    {
        path: 'company-updates',
        loadChildren: (): Promise<any> => import('./company-updates/company-updates.module').then(o => o.CompanyUpdatesModule),
        canActivate: [AuthGuard]
    },
    {
        path: 'settings',
        loadChildren: (): Promise<any> => import('./settings/settings.module').then(o => o.SettingsModule),
        canActivate: [AuthGuard]
    },
    {
        path: 'reset-password',
        loadChildren: (): Promise<any> => import('./reset-password/reset-password.module').then(o => o.ResetPasswordModule)
    },
    {
        path: 'access-denied',
        loadChildren: (): Promise<any> => import('./access-denied/access-denied.module').then(o => o.AccessDeniedModule)
    },
    {
        // Signed in with Entra but the API is down/restarting (deploy). Polls and resumes the
        // session automatically - deliberately unguarded, like access-denied.
        path: 'reconnecting',
        loadChildren: (): Promise<any> => import('./reconnecting/reconnecting.module').then(o => o.ReconnectingModule)
    },
    {
        path: 'pending-access',
        loadChildren: (): Promise<any> => import('./pending-access/pending-access.module').then(o => o.PendingAccessModule),
        canActivate: [AuthGuard]
    },
    {
        path: '**', redirectTo: 'home', pathMatch: 'full'
    }
];

@NgModule({
    imports: [RouterModule.forRoot(routes)],
    exports: [RouterModule]
})
export class AppRoutingModule { }
